import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, Search, UserRound, WifiOff } from 'lucide-react';
import { useMe, useLogout } from '@most/auth';
import { useLocale, SUPPORTED_LOCALES, type SupportedLocale } from '@most/i18n';
import { cn } from '@most/ui';
import { FlavorSwitch, ThemeToggle } from '@/components/bold/Bold';
import { Flag } from '@/components/common/Flag';
import { useLiveChannelEnabled, useWsStatus } from '@/lib/live';

const LOCALE_LABEL: Record<SupportedLocale, string> = {
  ru: 'Русский',
  en: 'English',
  tk: 'Türkmen',
};

/**
 * The live indicator is not decoration. The sidebar's counters are only trustworthy while the
 * websocket is up; if it drops, Merdan must know the numbers may be stale rather than believe an
 * empty queue.
 *
 * Which is why it is SILENT when healthy. The old bar showed a green "Обновления в реальном
 * времени" chip at all times — an always-on reassurance that everything is fine is just noise the
 * eye learns to skip, and the moment it turns into a warning the eye skips that too. A connected
 * socket is the expected state and says nothing; only the broken state speaks.
 *
 * It is equally silent when the server has NO live channel (`/config.live_channel === false`).
 * That is not a fault, it is a deployment without the feature, and the query layer compensates by
 * refetching on window focus. Warning about it would make the amber chip permanent — which is how
 * this indicator was previously stuck on "нет связи с сервером" on every single page load, and a
 * warning that is always lit is a warning nobody reads on the day it finally means something.
 */
function LiveIndicator() {
  const { t } = useTranslation();
  const status = useWsStatus();
  const liveEnabled = useLiveChannelEnabled();

  // No live channel on this server — say nothing. There is nothing for Merdan to act on.
  if (!liveEnabled) return null;

  if (status === 'open') {
    return (
      <span className="sr-only" role="status">
        {t('common.liveOn')}
      </span>
    );
  }

  const connecting = status === 'connecting';

  return (
    <span
      role="status"
      title={connecting ? t('common.liveConnecting') : t('common.liveOff')}
      className={cn(
        'flex items-center gap-1.5 rounded px-2 py-1 text-xs font-medium ring-1',
        connecting
          ? 'bg-muted text-muted-foreground ring-border'
          : 'bg-amber-50 text-amber-800 ring-amber-200',
      )}
    >
      <WifiOff className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span className="hidden lg:inline">
        {connecting ? t('common.liveConnecting') : t('common.liveOff')}
      </span>
      <span className="lg:hidden">
        {connecting ? t('common.liveConnecting') : t('common.liveOffShort')}
      </span>
    </span>
  );
}

/**
 * Both right-hand controls are menus, so the dismissal rules live here once instead of twice:
 * click outside closes, Escape closes AND returns focus to the trigger (otherwise focus is
 * stranded on a node that just unmounted, and the next Tab starts from the top of the document).
 *
 * `pointerdown` rather than `click`: a click that lands on the *other* trigger must close this
 * menu and open that one, and by the time `click` fires the other trigger's own handler has
 * already toggled it — so the two menus would fight and neither would open.
 */
function useMenu() {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      triggerRef.current?.focus();
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return { open, setOpen, rootRef, triggerRef };
}

/** The shared popover shell. Right-aligned under its trigger — both menus hang off the bar's edge. */
const MENU_PANEL = cn(
  'absolute right-0 top-[calc(100%+0.375rem)] z-30 min-w-[11rem] overflow-hidden',
  'rounded-lg border border-border bg-popover p-1 text-popover-foreground',
  'animate-scale-in',
);

const MENU_ITEM = cn(
  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm',
  'transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-none',
);

/**
 * The bar's own button shape. The header sits ON the canvas (see below), so its controls are
 * transparent until touched rather than being cards floating on a card — the previous bar drew a
 * bordered select box and a button, which read as a form someone had left in the corner.
 */
const BAR_BUTTON = cn(
  'flex h-9 items-center gap-1.5 rounded-lg px-2 text-sm text-muted-foreground',
  'transition-colors hover:bg-muted hover:text-foreground',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-app',
  'aria-expanded:bg-muted aria-expanded:text-foreground',
);

/**
 * The locale picker, as a segmented flag-tab strip — flag-only, no text.
 *
 * It was a full-width Select, then a globe-and-menu; both put a setting Merdan changes
 * approximately never behind a control at full weight. As a flag strip the three languages sit
 * side by side and the switch is one click, with no popover to open and dismiss.
 *
 * Semantics: a radiogroup, not a tablist — these buttons set a preference, they do not reveal
 * panels. A roving tabindex keeps the strip one tab-stop; arrow keys move (and select).
 */
function LocaleMenu() {
  const { t } = useTranslation();
  const { locale, setLocale } = useLocale();
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  const move = (to: number) => {
    const n = SUPPORTED_LOCALES.length;
    const idx = ((to % n) + n) % n;
    const next = SUPPORTED_LOCALES[idx];
    if (!next) return;
    refs.current[idx]?.focus();
    setLocale(next);
  };

  const onKeyDown = (e: React.KeyboardEvent, i: number) => {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        move(i + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        move(i - 1);
        break;
      case 'Home':
        e.preventDefault();
        move(0);
        break;
      case 'End':
        e.preventDefault();
        move(SUPPORTED_LOCALES.length - 1);
        break;
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label={t('nav.language')}
      className="inline-flex items-center gap-0.5 rounded-lg bg-muted/60 p-0.5"
    >
      {SUPPORTED_LOCALES.map((l, i) => {
        const selected = l === locale;
        return (
          <button
            key={l}
            ref={(el) => (refs.current[i] = el)}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={LOCALE_LABEL[l]}
            title={LOCALE_LABEL[l]}
            tabIndex={selected ? 0 : -1}
            onClick={() => l !== locale && setLocale(l)}
            onKeyDown={(e) => onKeyDown(e, i)}
            className={cn(
              'flex h-7 w-8 items-center justify-center rounded-md transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-app',
              selected
                ? 'bg-card shadow-sm ring-1 ring-black/5'
                : 'opacity-60 hover:bg-card/50 hover:opacity-100',
            )}
          >
            <Flag locale={l} />
          </button>
        );
      })}
    </div>
  );
}

/**
 * The account menu — avatar, and behind it the identity and the way out.
 *
 * `Выйти` used to sit permanently in the bar as a labelled button, one tab-stop from everything
 * else. Logout is the single most destructive control on the screen and it was the easiest one to
 * hit by accident; putting it inside a menu costs a deliberate click and buys back the corner.
 * The email moves in with it, because the email is not a control — it answers "who am I logged in
 * as", a question asked once a session, and it does not need to occupy 13rem to do it.
 */
function AccountMenu() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const logout = useLogout();
  const navigate = useNavigate();
  const { open, setOpen, rootRef, triggerRef } = useMenu();
  const id = useId();

  if (!me) return null;

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        id={id}
        aria-label={me.email}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cn(BAR_BUTTON, 'gap-2 pl-1 pr-2')}
      >
        <span
          aria-hidden="true"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-rail text-2xs font-semibold uppercase text-rail-foreground"
        >
          {me.email.slice(0, 2)}
        </span>
        <ChevronDown
          className={cn('h-3.5 w-3.5 shrink-0 transition-transform', open && 'rotate-180')}
          aria-hidden="true"
        />
      </button>

      {open ? (
        <div role="menu" aria-labelledby={id} className={cn(MENU_PANEL, 'min-w-[14rem]')}>
          <div className="border-b border-border px-2 pb-2 pt-1.5">
            <p className="truncate text-sm font-medium text-foreground" title={me.email}>
              {me.email}
            </p>
            <p className="text-2xs uppercase tracking-wide text-muted-foreground">
              {t(`status.role.${me.role}`)}
            </p>
          </div>

          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              navigate('/profile');
            }}
            className={cn(MENU_ITEM, 'mt-1')}
          >
            <UserRound className="h-4 w-4 shrink-0" aria-hidden="true" />
            {t('nav.profile')}
          </button>

          <button
            type="button"
            role="menuitem"
            onClick={() => logout.mutate()}
            disabled={logout.isPending}
            className={cn(
              MENU_ITEM,
              'text-destructive hover:bg-destructive/10 focus-visible:bg-destructive/10',
              'disabled:cursor-not-allowed disabled:opacity-60',
            )}
          >
            <LogOut className="h-4 w-4 shrink-0" aria-hidden="true" />
            {t('nav.logout')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** The ⌘K affordance. A palette nobody knows about is a palette nobody uses. */
function PaletteTrigger({ onOpen }: { onOpen: () => void }) {
  const { t } = useTranslation();

  // navigator.platform is deprecated but is still the only synchronous read of "is this a Mac",
  // and getting the modifier glyph wrong is worse than the deprecation.
  const isMac =
    typeof navigator !== 'undefined' && /mac|iphone|ipad/i.test(navigator.platform ?? '');

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'flex h-9 w-[16rem] items-center gap-2 rounded border border-border bg-card px-3',
        'text-sm text-muted-foreground shadow-card transition-colors',
        'hover:border-input hover:bg-muted',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      )}
    >
      <Search className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="flex-1 text-left">{t('palette.open')}</span>
      <kbd className="shrink-0 rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-2xs">
        {isMac ? '⌘' : 'Ctrl'}K
      </kbd>
    </button>
  );
}

export function TopBar({ onOpenPalette }: { onOpenPalette: () => void }) {
  return (
    /*
     * The bar sits ON the canvas — no card background, no bottom rule. The workspace panel already
     * has an edge (it is inset into the dark shell), so a second border here would be a line
     * drawn beside a line. `bg-app/80 + backdrop-blur` keeps the rows legible as they scroll under
     * it while the bar itself stays part of the canvas rather than becoming a shelf on top of it.
     */
    <header className="sticky top-0 z-20 flex h-16 shrink-0 items-center gap-3 bg-app/80 px-6 backdrop-blur">
      <PaletteTrigger onOpen={onOpenPalette} />

      <LiveIndicator />

      <div className="flex-1" />

      {/*
       * Two controls where there were four. The eye going to the top-right corner of a work screen
       * is looking for "who am I / how do I get out", and the old row answered that with a form
       * control, a raw string, and a button all at the same weight — so it answered it slowly.
       */}
      <div className="flex items-center gap-2">
        <FlavorSwitch />
        <ThemeToggle />
        <LocaleMenu />
        <AccountMenu />
      </div>
    </header>
  );
}
